import { ArrowRightOutlined } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import AuthContext from 'context/AuthContext';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { UrlHelpers } from 'urlHelpers/urlHelper';

import Loader from 'components/Loader/Loader';

const NewInvestigation = ({ onBack, refreshAlerts }) => {
  const [submitting, setSubmitting] = useState(false);
  const [investigationError, setInvestigationError] = useState('');
  const [showCuratedAlarms, setShowCuratedAlarms] = useState(false);
  const [result, setResult] = useState(null);
  const { accessToken } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    alarmName: '',
    application: 'OIL-API-FVL',
    failedReason: '',
    errorDescription: '',
    criticality: 'MEDIUM',
    logExcerpt: '',
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!formData.alarmName.trim()) {
      newErrors.alarmName = 'Alarm name is required';
    }

    if (!formData.logExcerpt.trim()) {
      newErrors.logExcerpt = 'A log excerpt is required';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const CURATED_KB_ALARMS = [
    'OIL-API-FVL-AccountDetailsNotInRedis',
    'OIL-API-FVL-AccountNotInRedis',
    'OIL-API-FVL-AccountsNotAssociated',
    'OIL-API-FVL-CustomerDetailsNotInRedis',
    'OIL-API-FVL-NoEligibleAccounts',
    'OIL-API-FVL-PegaAPIError',
    'OIL-API-FVL-PegaCaseCreationFailed',
    'OIL-API-FVL-PegaErrorResponse',
    'OIL-API-FVL-PrefetchTimeout',
  ];

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSubmitting(true);
      setInvestigationError('');

      const payload = {
        alarmId: crypto.randomUUID(),
        alarmName: formData.alarmName,
        application: formData.application,
        failedReason: formData.failedReason,
        errorDescription: formData.errorDescription,
        criticality: formData.criticality,
      };

      // CREATE ALERT
      const createResponse = await fetch(UrlHelpers.autopsCreateAlert, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          appid: 'notifyadmin',
        },
        body: JSON.stringify(payload),
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create alert');
      }

      const alertData = await createResponse.json();

      const { alertId } = alertData;

      const investigateResponse = await fetch(UrlHelpers.autopsInvestigateAlert(alertId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          appid: 'notifyadmin',
        },
        body: JSON.stringify({
          logExcerpt: formData.logExcerpt,
          alarmName: formData.alarmName,
          application: formData.application,
          failedReason: formData.failedReason,
          errorDescription: formData.errorDescription,
          criticality: formData.criticality,
        }),
      });

      if (!investigateResponse.ok) {
        setInvestigationError(`Investigation could not be started: ${investigateResponse.status}`);

        setSubmitting(false);
        return;
      }

      const investigationResult = await investigateResponse.json();
      setResult(investigationResult);
      if (refreshAlerts) {
        refreshAlerts();
      }
    } catch (error) {
      console.error(error);
      setInvestigationError('Failed to start investigation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          variant="outlined"
          sx={{ mb: 2 }}
          onClick={() => {
            setResult(null);

            setFormData({
              alarmName: '',
              application: 'OIL-API-FVL',
              failedReason: '',
              errorDescription: '',
              criticality: 'MEDIUM',
              logExcerpt: '',
            });

            setErrors({});
            setInvestigationError('');
          }}
        >
          ← Start a New Investigation
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Loader open={submitting} />
      <Paper
        sx={{
          maxWidth: 900,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Typography sx={{ mb: 3, color: '#6b7280', fontSize: '13px !important' }}>
          Submit a genuinely new error — one not already curated in the Knowledge Base — to watch
          the same live pipeline investigate it from scratch. Avoid these existing KB alarm names if
          you want a true unseen-failure test:
        </Typography>

        <Box
          onClick={() => setShowCuratedAlarms(!showCuratedAlarms)}
          sx={{
            fontSize: 13,
            color: '#666',
            mb: 2,
            cursor: 'pointer',
            fontWeight: 600,
            display: 'flex',
            flexDirection: 'row',
            gap: '5px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {showCuratedAlarms ? <ArrowDropDownIcon /> : <ArrowRightOutlined />}
          </Box>
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            Curated KB alarm names (avoid these for an unseen test)
          </Typography>
        </Box>

        {showCuratedAlarms && (
          <Box
            component="ul"
            sx={{
              mt: 1,
              pl: 4,
              color: '#6b7280',
            }}
          >
            {CURATED_KB_ALARMS.map((alarm) => (
              <Box
                component="li"
                key={alarm}
                sx={{
                  mb: 1.5,
                  fontSize: '14px',
                }}
              >
                {alarm}
              </Box>
            ))}
          </Box>
        )}

        {investigationError && (
          <Box
            sx={{
              mb: 3,
              color: '#d32f2f',
              border: '1px solid #ef9a9a',
              backgroundColor: '#fdeaea',
              borderRadius: 2,
              padding: 2,
            }}
          >
            {investigationError}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box>
            <Typography sx={{ mb: 1 }}>Alarm name *</Typography>

            <TextField
              fullWidth
              placeholder="OIL-Demo-YourNewAlarm"
              value={formData.alarmName}
              error={!!errors.alarmName}
              helperText={errors.alarmName}
              onChange={(e) => handleChange('alarmName', e.target.value)}
            />
          </Box>

          <Box>
            <Typography sx={{ mb: 1 }}>Application</Typography>

            <TextField
              fullWidth
              value={formData.application}
              onChange={(e) => handleChange('application', e.target.value)}
            />
          </Box>

          <Box>
            <Typography sx={{ mb: 1 }}>Failed reason</Typography>

            <TextField
              fullWidth
              placeholder="Short description of what failed"
              value={formData.failedReason}
              onChange={(e) => handleChange('failedReason', e.target.value)}
            />
          </Box>

          <Box>
            <Typography sx={{ mb: 1 }}>Error description</Typography>

            <TextField
              fullWidth
              value={formData.errorDescription}
              onChange={(e) => handleChange('errorDescription', e.target.value)}
            />
          </Box>

          <Box>
            <Typography sx={{ mb: 1 }}>Criticality</Typography>

            <FormControl fullWidth>
              <Select
                value={formData.criticality}
                onChange={(e) => handleChange('criticality', e.target.value)}
              >
                <MenuItem value="HIGH">HIGH</MenuItem>
                <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                <MenuItem value="LOW">LOW</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={{ mb: 1 }}>Log Expert *</Typography>
            <TextareaAutosize
              minRows={6}
              value={formData.logExcerpt}
              placeholder="2026-07-08 09:00:00,000 - ERROR - TimeoutError: connection timed out"
              onChange={(e) => handleChange('logExcerpt', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: '4px',
                border: errors.logExcerpt ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            {errors.logExcerpt && (
              <Typography
                variant="caption"
                sx={{
                  color: '#d32f2f',
                  display: 'block',
                  mt: 0.5,
                  ml: '14px',
                }}
              >
                {errors.logExcerpt}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <CircularProgress
                    size={18}
                    sx={{
                      color: '#fff',
                      mr: 1,
                    }}
                  />
                  Running Investigation...
                </>
              ) : (
                'Run Investigation'
              )}
            </Button>

            <Button variant="outlined" onClick={onBack}>
              Back
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

NewInvestigation.propTypes = {
  onBack: PropTypes.func.isRequired,
  refreshAlerts: PropTypes.func,
};

export default NewInvestigation;
